import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { 
  Waves, Sparkles, Utensils, Wifi, Car, Flame, MapPin, Star, ChevronRight, 
  Menu, X, Users, Bed, Bath, Key, PawPrint, ChevronLeft, ExternalLink, Phone, Search, Calendar, UserPlus
} from "lucide-react";
import { PROPERTY_DATA, ROOMS_DATA, REVIEWS_DATA, ATTRACTIONS_DATA } from "./data";

// Navigation - Dark navbar with accent border
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'HOME', href: '#' },
    { label: 'AMENITIES', href: '#amenities' },
    { label: 'ROOMS', href: '#suites' },
    { label: 'AREA', href: '#area' },
    { label: 'CONTACT', href: '#contact' }
  ];

  return (
    <nav data-testid="main-navigation" className={`fixed top-0 left-0 right-0 z-50 bg-primary border-b-4 border-accent transition-all duration-300 ${scrolled ? 'shadow-lg' : ''}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <a href="#" className="flex-shrink-0" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_grand-bend-lodging/artifacts/se79dcmt_logo_white_cropped.png" 
              alt="South Shore Suites" 
              className="h-14 lg:h-[72px] w-auto"
            />
          </a>

          {/* Desktop Nav - Centered */}
          <div className="hidden lg:flex items-center gap-10">
            {navItems.map(item => (
              <a 
                key={item.href} 
                href={item.href} 
                className="text-[11px] font-semibold text-white/80 tracking-[0.15em] hover:text-accent transition-colors relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full"></span>
              </a>
            ))}
          </div>
          
          {/* Right side - Phone & Book */}
          <div className="hidden lg:flex items-center gap-6">
            <a href="tel:2262221757" className="flex items-center gap-2 text-sm text-white/80 hover:text-accent transition-colors whitespace-nowrap">
              <Phone size={15} strokeWidth={1.5} />
              <span className="font-medium tracking-wide">(226) 222-1757</span>
            </a>
            
            <a href="https://hotel2743.openhotel.com/results.cfm" target="_blank" rel="noopener noreferrer" data-testid="nav-book-btn" className="btn-accent whitespace-nowrap">
              Book Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 text-white" data-testid="mobile-menu-btn">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="lg:hidden bg-primary border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-1">
            {navItems.map(item => (
              <a key={item.href} href={item.href} onClick={() => setIsOpen(false)} className="block py-3 text-sm font-semibold text-white/80 tracking-widest border-b border-white/10 hover:text-accent">
                {item.label}
              </a>
            ))}
            <div className="pt-6">
              <a href="tel:2262221757" className="flex items-center gap-2 text-sm text-white/80 mb-4">
                <Phone size={16} />
                (226) 222-1757
              </a>
              <a href="https://hotel2743.openhotel.com/results.cfm" target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className="block btn-accent text-center">
                Book Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section - Whispering Pines Style
const HeroSection = ({ property }) => {
  const [currentBgImage, setCurrentBgImage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get the right images based on screen size
  const images = isMobile 
    ? (property?.property_images_mobile || property?.property_images) 
    : property?.property_images;

  useEffect(() => {
    if (!images?.length) return;
    const interval = setInterval(() => {
      setCurrentBgImage(prev => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images]);

  return (
    <section data-testid="hero-section" className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Fading Background Images */}
      <div className="absolute inset-0 overflow-hidden">
        {images?.map((img, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ${idx === currentBgImage ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={img} alt={`Property ${idx + 1}`} className="w-full h-full object-cover scale-110 object-center" />
          </div>
        ))}
        <div className="absolute inset-0 bg-black/40" />
      </div>
      
      <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4">
        <h1 className="font-serif text-5xl md:text-7xl font-semibold mb-6 text-white tracking-wide">
          {property?.name || "South Shore Suites"}
        </h1>
        <p className="text-xl md:text-2xl mb-10 text-white/90 font-light italic">
          {property?.tagline || "Your Perfect Bayfield Getaway"}
        </p>
        
        <a 
          href="https://hotel2743.openhotel.com/results.cfm" 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn-accent px-12 py-4 text-lg font-semibold tracking-wide"
        >
          Book Now
        </a>

        {/* Image indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {images?.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentBgImage(idx)}
              className={`w-3 h-3 rounded-full transition-colors ${idx === currentBgImage ? 'bg-white' : 'bg-white/40'}`}
              aria-label={`View image ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Welcome Section
const WelcomeSection = ({ property }) => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-4xl md:text-5xl text-primary mb-2">Welcome to South Shore Suites</h2>
            <div className="w-12 h-1 bg-accent mb-6"></div>
            <p className="text-accent italic text-lg mb-6">Your Family-Friendly Getaway in Bayfield</p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {property?.description || "At South Shore Suites, we're more than just a place to stay – we're a family-friendly retreat nestled in the heart of Bayfield, where comfort meets convenience. Whether you're here to relax, explore the beautiful shores of Lake Huron, or enjoy the vibrant local attractions, our suites offer the perfect home base for your vacation."}
            </p>
            <a href="https://hotel2743.openhotel.com/results.cfm" target="_blank" rel="noopener noreferrer" className="btn-accent inline-block">
              Book Your Stay
            </a>
          </div>
          <div className="overflow-hidden">
            <img 
              src={property?.property_images?.[0] || "https://customer-assets.emergentagent.com/job_grand-bend-lodging/artifacts/tflxtxu8_IMG_7246.JPG"} 
              alt="Pool at South Shore Suites"
              className="w-full h-[400px] object-cover object-center scale-125 shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Room Detail Modal
const RoomDetailModal = ({ room, onClose }) => {
  const [currentImage, setCurrentImage] = useState(0);

  if (!room) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="room-modal">
      <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="font-serif text-2xl text-primary">{room.name}</h2>
            <p className="text-sm text-muted-foreground">{room.subtitle}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-modal">
            <X size={24} />
          </button>
        </div>

        <div className="relative">
          <img src={room.images[currentImage]} alt={room.name} className="w-full aspect-video object-cover" />
          <button onClick={() => setCurrentImage((prev) => (prev - 1 + room.images.length) % room.images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg">
            <ChevronLeft size={24} />
          </button>
          <button onClick={() => setCurrentImage((prev) => (prev + 1) % room.images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg">
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="flex gap-2 p-4 overflow-x-auto">
          {room.images.map((img, idx) => (
            <button key={idx} onClick={() => setCurrentImage(idx)} className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 ${idx === currentImage ? 'border-accent' : 'border-transparent'}`}>
              <img src={img} alt={`${room.name} ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        <div className="p-6">
          <div className="flex flex-wrap gap-6 mb-6 pb-6 border-b">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users size={20} /> Sleeps {room.sleeps}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Bed size={20} /> {room.beds}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-serif text-xl mb-3">About This Suite</h3>
            <p className="text-muted-foreground leading-relaxed">{room.description}</p>
          </div>

          {room.note && (
            <div className="mb-6 bg-amber-50 border border-amber-200 p-4">
              <p className="text-amber-800 text-sm"><strong>Please Note:</strong> {room.note}</p>
            </div>
          )}

          <div className="mb-6">
            <h3 className="font-serif text-xl mb-3">Suite Features</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {room.features?.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-accent rounded-full" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href="https://hotel2743.openhotel.com/results.cfm" target="_blank" rel="noopener noreferrer" className="flex-1 bg-accent hover:bg-accent/90 text-white text-center py-4 font-medium transition-colors flex items-center justify-center gap-2" data-testid="modal-book-btn">
              Book Now <ExternalLink size={18} />
            </a>
            <a href="#contact" onClick={onClose} className="flex-1 border-2 border-primary text-primary hover:bg-primary hover:text-white text-center py-4 font-medium transition-colors flex items-center justify-center">
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Room Card - Whispering Pines Style
const RoomCard = ({ room, onViewDetails }) => {
  return (
    <div className="bg-white shadow-lg overflow-hidden" data-testid={`room-card-${room.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
        <img src={room.images[0]} alt={room.name} className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500" />
        {room.badge && (
          <div className="absolute top-4 left-4 bg-accent text-white text-xs px-3 py-1 font-medium tracking-wider uppercase">
            {room.badge}
          </div>
        )}
      </div>
      
      <div className="p-6 bg-secondary">
        <h3 className="font-serif text-2xl text-primary mb-1">{room.name}</h3>
        <p className="text-sm text-muted-foreground mb-4">Sleeps {room.sleeps} • {room.beds}</p>
        
        <div className="flex items-center justify-end">
          <button 
            onClick={() => onViewDetails(room)}
            className="text-accent text-sm font-medium tracking-wider uppercase flex items-center gap-1 hover:underline"
            data-testid={`view-more-${room.id}`}
          >
            <Search size={14} /> View Details
          </button>
        </div>
      </div>
    </div>
  );
};

// Suites Section - Whispering Pines Style
const SuitesSection = ({ rooms }) => {
  const [selectedRoom, setSelectedRoom] = useState(null);

  return (
    <section id="suites" data-testid="suites-section" className="py-20 bg-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-primary accent-underline">Rooms & Rates</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rooms?.map(room => (
            <RoomCard key={room.id} room={room} onViewDetails={setSelectedRoom} />
          ))}
        </div>
      </div>

      {selectedRoom && (
        <RoomDetailModal room={selectedRoom} onClose={() => setSelectedRoom(null)} />
      )}
    </section>
  );
};

// Amenities Section
const AmenitiesSection = ({ property }) => {
  const iconMap = {
    waves: Waves, sparkles: Sparkles, flame: Flame, car: Car,
    utensils: Utensils, paw: PawPrint, wifi: Wifi, key: Key
  };

  return (
    <section id="amenities" data-testid="amenities-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src={property?.property_images?.[1] || "https://customer-assets.emergentagent.com/job_grand-bend-lodging/artifacts/rrg83qnw_IMG_7221.jpg"} 
              alt="Hot tub"
              className="w-full h-auto shadow-lg"
            />
          </div>
          <div>
            <h2 className="font-serif text-4xl md:text-5xl text-primary mb-2">Comfortable Accommodations</h2>
            <div className="w-12 h-1 bg-accent mb-6"></div>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Our spacious suites are designed with families in mind, offering a range of options to suit your needs. Every suite is equipped with modern amenities, including complimentary High Speed Wi-Fi, Smart TVs, full kitchens, and air conditioning, ensuring a comfortable and enjoyable experience for guests of all ages.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-8">
              {property?.shared_amenities?.slice(0, 6).map((amenity, idx) => {
                const Icon = iconMap[amenity.icon] || Sparkles;
                return (
                  <div key={idx} className="flex items-center gap-3" data-testid={`amenity-${idx}`}>
                    <Icon size={20} className="text-accent" />
                    <span className="text-sm">{amenity.name}</span>
                  </div>
                );
              })}
            </div>

            <a href="https://hotel2743.openhotel.com/results.cfm" target="_blank" rel="noopener noreferrer" className="btn-accent inline-block">
              Book Your Stay
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = ({ reviews }) => {
  const [currentSet, setCurrentSet] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const totalSets = 4;
  
  // Get reviews for current set
  const currentReviews = reviews?.featured_reviews?.filter(r => r.set === currentSet) || [];
  
  // Auto-advance carousel - 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSet(prev => prev >= totalSets ? 1 : prev + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const goToSet = (setNum) => {
    setCurrentSet(setNum);
  };

  const nextSet = () => {
    setCurrentSet(prev => prev >= totalSets ? 1 : prev + 1);
  };

  const prevSet = () => {
    setCurrentSet(prev => prev <= 1 ? totalSets : prev - 1);
  };

  // Platform icon component
  const PlatformBadge = ({ platform }) => {
    const colors = {
      'Google': 'bg-white text-gray-800',
      'Airbnb': 'bg-[#FF5A5F] text-white',
      'Booking.com': 'bg-[#003580] text-white'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${colors[platform] || 'bg-gray-500 text-white'}`}>
        {platform}
      </span>
    );
  };

  // Review Modal
  const ReviewModal = ({ review, onClose }) => {
    if (!review) return null;
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-primary max-w-2xl w-full rounded-lg p-8 relative" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white">
            <X size={24} />
          </button>
          <div className="flex items-center justify-between mb-4">
            <div className="flex">
              {[...Array(review.rating)].map((_, i) => (
                <Star key={i} size={20} fill="#C4A574" className="text-accent" />
              ))}
            </div>
            <PlatformBadge platform={review.platform} />
          </div>
          <p className="text-white/90 text-base leading-relaxed italic mb-6">"{review.text}"</p>
          <p className="font-medium text-accent text-lg">— {review.name}</p>
        </div>
      </div>
    );
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-serif text-4xl md:text-5xl mb-4">What Our Guests Say</h2>
          <div className="w-12 h-1 bg-accent mx-auto mb-6"></div>
          <div className="flex items-center justify-center gap-2 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={24} fill="#C4A574" className="text-accent" />
            ))}
            <span className="text-2xl font-semibold ml-2">200+</span>
            <span className="text-white/70">reviews</span>
          </div>
          
          {/* Platform Logos */}
          <div className="flex items-center justify-center gap-8 md:gap-14 mt-8 flex-wrap">
            {/* Airbnb */}
            <a 
              href="https://www.airbnb.ca/rooms/1208281063468906113?viralityEntryPoint=1&s=76"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_load-preview-7/artifacts/orh7402k_image.png" 
                alt="Airbnb" 
                className="h-12 object-contain"
              />
            </a>
            {/* Booking.com */}
            <a 
              href="https://www.booking.com/hotel/ca/south-shore-resort.en-gb.html?label=gen173nr-10CAEoggI46AdIM1gEaCeIAQGYATO4ARfIAQzYAQPoAQH4AQGIAgGoAgG4AoD__MwGwAIB0gIkMGE5N2VkYmUtOTFkNC00M2UzLWE2OTctM2QxYzUwNjY0ZWNh2AIB4AIB&aid=304142&ucfs=1&arphpl=1&checkin=2026-07-09&checkout=2026-07-10"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_load-preview-7/artifacts/mbjb1hj2_image.png" 
                alt="Booking.com" 
                className="h-12 object-contain"
              />
            </a>
            {/* Expedia */}
            <a 
              href="https://www.expedia.ca/Bluewater-Hotels-South-Shore-Suites.h113501915.Hotel-Information?chkin=2026-06-11&chkout=2026-06-12&flexibility=1_DAY&searchRange=2026-06-01_2026-06-30&x_pwa=1&rfrr=HSR&pwa_ts=1772044257679&referrerUrl=aHR0cHM6Ly93d3cuZXhwZWRpYS5jYS9Ib3RlbC1TZWFyY2g%3D&useRewards=true&rm1=a2&regionId=6336300&destination=Bayfield%2C%20Ontario%2C%20Canada&destType=MARKET&latLong=43.560998%2C-81.695903&sort=RECOMMENDED&top_dp=211&top_cur=CAD&semcid=CA.UB.BING.DT-c-EN.HOTEL&semdtl=a1392824997.b11326012149757436.g1kwd-82876520415887%3Aloc-32.e1c.m1fb60c425cb4a162e5a20adbaf48fc573.r129b0fdf5f6a895498d5325f66aca1d9f1c9d23fb6aa45333a3a2bb6e04f0b384.c1.j1124010.k1116159.d182876027304962.h1e.i1.l1.n1.o1.p1.q1.s1.t1.x1.f1.u1.v1.w1&userIntent=&selectedRoomType=326214744&selectedRatePlan=404392327&searchId=quick-load-16"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_load-preview-7/artifacts/4dyn3ewo_image.png" 
                alt="Expedia" 
                className="h-12 object-contain"
              />
            </a>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button 
            onClick={prevSet}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full transition-all"
            aria-label="Previous reviews"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>
          <button 
            onClick={nextSet}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full transition-all"
            aria-label="Next reviews"
          >
            <ChevronRight size={24} className="text-white" />
          </button>

          {/* Reviews Grid */}
          <div className="grid md:grid-cols-3 gap-6 transition-all duration-500">
            {currentReviews.map((review, idx) => (
              <div 
                key={`${currentSet}-${idx}`} 
                className="bg-white/10 backdrop-blur-sm p-6 rounded-lg transform transition-all duration-500 hover:bg-white/15" 
                data-testid={`review-${idx}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="#C4A574" className="text-accent" />
                    ))}
                  </div>
                  <PlatformBadge platform={review.platform} />
                </div>
                <p className="text-white/90 mb-3 text-sm leading-relaxed italic line-clamp-4">"{review.text}"</p>
                <button 
                  onClick={() => setSelectedReview(review)}
                  className="text-accent text-sm hover:underline mb-3 block"
                >
                  Read More
                </button>
                <p className="font-medium text-accent">— {review.name}</p>
              </div>
            ))}
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-3 mt-8">
            {[1, 2, 3, 4].map((setNum) => (
              <button
                key={setNum}
                onClick={() => goToSet(setNum)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSet === setNum 
                    ? 'bg-accent w-8' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to review set ${setNum}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {selectedReview && (
        <ReviewModal review={selectedReview} onClose={() => setSelectedReview(null)} />
      )}
    </section>
  );
};

// Area Section
const AreaSection = ({ attractions, property }) => {
  return (
    <section id="area" data-testid="area-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-4xl md:text-5xl text-primary mb-2">Close to Grand Bend's Best</h2>
            <div className="w-12 h-1 bg-accent mb-6"></div>
            <p className="text-muted-foreground leading-relaxed mb-6">
              When you stay with us, you're just minutes away from the best that the area has to offer. Spend the day soaking up the sun at the famous Grand Bend Beach, or take a short drive to the Pinery Provincial Park for hiking, bird-watching, and more outdoor adventures.
            </p>
            
            <div className="space-y-4 mb-8">
              {attractions?.beaches?.slice(0, 3).map((beach, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <MapPin size={20} className="text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium">{beach.name}</h4>
                    <p className="text-sm text-muted-foreground">{beach.distance} — {beach.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <a href="#contact" className="btn-accent inline-block">
              Contact Us
            </a>
          </div>
          <div>
            <img 
              src={property?.property_images?.[2] || "https://customer-assets.emergentagent.com/job_grand-bend-lodging/artifacts/ozlephii_IMG_7277.JPG"} 
              alt="Deck at night"
              className="w-full h-auto shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="py-20 bg-secondary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-serif text-4xl md:text-5xl text-primary mb-4">Ready to Book Your Bayfield Vacation?</h2>
        <div className="w-12 h-1 bg-accent mx-auto mb-8"></div>
        <p className="text-muted-foreground text-lg mb-12 max-w-2xl mx-auto">
          Experience the perfect getaway at South Shore Suites. Just 15 minutes from Grand Bend Beach with pool, hot tub, and all the amenities you need.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
          <a 
            href="tel:2262221757" 
            className="flex items-center gap-3 bg-primary text-white px-8 py-4 text-lg font-medium hover:bg-primary/90 transition-colors"
          >
            <Phone size={24} />
            Call Now: (226) 222-1757
          </a>
          <a 
            href="https://hotel2743.openhotel.com/results.cfm" 
            target="_blank"
            rel="noopener noreferrer"
            className="btn-accent px-8 py-4 text-lg"
          >
            Book Your Stay
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ property }) => {
  return (
    <footer data-testid="footer" className="bg-primary text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_grand-bend-lodging/artifacts/se79dcmt_logo_white_cropped.png" 
              alt="South Shore Suites" 
              className="h-24 w-auto mb-4"
            />
            <p className="text-white/70 text-sm mb-2">{property?.address}</p>
            <a href="tel:2262221757" className="text-accent hover:underline block mb-1">(226) 222-1757</a>
            <a href="mailto:Booksouthshore@gmail.com" className="text-accent hover:underline text-sm">Booksouthshore@gmail.com</a>
          </div>
          <div>
            <h4 className="font-medium mb-4 tracking-wider text-sm">QUICK LINKS</h4>
            <div className="space-y-2 text-sm text-white/70">
              <a href="https://hotel2743.openhotel.com/results.cfm" target="_blank" rel="noopener noreferrer" className="block hover:text-white">Book Now</a>
              <a href="#suites" className="block hover:text-white">Accommodations</a>
              <a href="#amenities" className="block hover:text-white">Amenities</a>
              <a href="#area" className="block hover:text-white">Area Attractions</a>
              <a href="#contact" className="block hover:text-white">Contact Us</a>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-4 tracking-wider text-sm">NEARBY</h4>
            <div className="space-y-2 text-sm text-white/70">
              <p>15 min to Grand Bend Beach</p>
              <p>5 min to Bayfield Pier</p>
              <p>20 min to Pinery Provincial Park</p>
              <p>2 min to Downtown Bayfield</p>
            </div>
          </div>
        </div>
        <div className="border-t border-white/20 pt-8 text-center text-sm text-white/50">
          <p>© {new Date().getFullYear()} South Shore Suites. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  // Use static data directly - no API calls needed
  const property = PROPERTY_DATA;
  const rooms = ROOMS_DATA;
  const reviews = REVIEWS_DATA;
  const attractions = ATTRACTIONS_DATA;

  return (
    <div className="App">
      <Navigation />
      <HeroSection property={property} />
      <WelcomeSection property={property} />
      <SuitesSection rooms={rooms} />
      <AmenitiesSection property={property} />
      <ReviewsSection reviews={reviews} />
      <AreaSection attractions={attractions} property={property} />
      <CTASection />
      <Footer property={property} />
    </div>
  );
}

export default App;
